import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchTenants, type TenantSummary } from '@/api/superadmin';
import { Button } from '@/components/primitives/button';
import { Input } from '@/components/primitives/input';
import { Skeleton } from '@/components/primitives/skeleton';
import { CreateTenantSheet } from '@/components/superadmin/create-tenant-sheet';
import { SuperAdminPage } from '@/components/superadmin/superadmin-layout';

const PAGE_SIZE = 20;

function AdminStatusBadge({ status }: { status: TenantSummary['adminStatus'] }) {
  if (!status) {
    return <span className="text-paragraph-xs text-text-soft">No administrator</span>;
  }

  const styles: Record<string, string> = {
    active: 'bg-success-base/10 text-success-base',
    invited: 'bg-warning-base/10 text-warning-base',
    invite_expired: 'bg-error-base/10 text-error-base',
  };

  const labels: Record<string, string> = {
    active: 'Active',
    invited: 'Invited',
    invite_expired: 'Invite expired',
  };

  return (
    <span className={`text-paragraph-xs rounded-full px-2 py-0.5 ${styles[status] ?? ''}`}>
      {labels[status] ?? status}
    </span>
  );
}

export function SuperAdminTenantsPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [tenants, setTenants] = useState<TenantSummary[] | null>(null);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  const load = useCallback(async () => {
    setError(null);

    try {
      const result = await fetchTenants({ search: search || undefined, page, limit: PAGE_SIZE });
      setTenants(result.tenants);
      setTotal(result.total);
    } catch (caught) {
      setError((caught as Error).message);
      setTenants([]);
    }
  }, [search, page]);

  useEffect(() => {
    // Debounced so typing in the search box does not fire a request per keystroke.
    const timer = setTimeout(() => void load(), 250);

    return () => clearTimeout(timer);
  }, [load]);

  const lastPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1);

  return (
    <SuperAdminPage
      title="Tenants"
      description="Every organization on this deployment, including ones created before the console existed."
      actions={
        <Button variant="primary" mode="filled" onClick={() => setSheetOpen(true)}>
          New tenant
        </Button>
      }
    >
      <Input
        value={search}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          setPage(0);
          setSearch(e.target.value);
        }}
        placeholder="Search by name"
        className="max-w-sm"
      />

      {error && <p className="text-paragraph-sm text-error-base">{error}</p>}

      <div className="border-stroke-soft bg-bg-white overflow-hidden rounded-xl border">
        <table className="w-full text-left">
          <thead className="border-stroke-soft bg-bg-weak border-b">
            <tr>
              <th className="text-label-xs text-text-soft px-5 py-3 font-medium">Tenant</th>
              <th className="text-label-xs text-text-soft px-5 py-3 font-medium">Administrator</th>
              <th className="text-label-xs text-text-soft px-5 py-3 font-medium">Subscribers</th>
              <th className="text-label-xs text-text-soft px-5 py-3 font-medium">Last activity</th>
              <th className="text-label-xs text-text-soft px-5 py-3 font-medium">Created</th>
            </tr>
          </thead>
          <tbody>
            {tenants === null && (
              <tr>
                <td colSpan={5} className="px-5 py-6">
                  <Skeleton className="h-5 w-full" />
                </td>
              </tr>
            )}

            {tenants?.length === 0 && (
              <tr>
                <td colSpan={5} className="text-paragraph-sm text-text-soft px-5 py-8 text-center">
                  {search ? 'No tenants match that search.' : 'No tenants yet.'}
                </td>
              </tr>
            )}

            {tenants?.map((tenant) => (
              <tr
                key={tenant._id}
                onClick={() => navigate(`/superadmin/tenants/${tenant._id}`)}
                className="border-stroke-soft hover:bg-bg-weak cursor-pointer border-b last:border-b-0"
              >
                <td className="text-paragraph-sm text-text-strong px-5 py-3">{tenant.name}</td>
                <td className="px-5 py-3">
                  <div className="flex flex-col gap-1">
                    <span className="text-paragraph-sm text-text-sub">{tenant.adminEmail ?? '—'}</span>
                    <AdminStatusBadge status={tenant.adminStatus} />
                  </div>
                </td>
                <td className="text-paragraph-sm text-text-sub px-5 py-3 tabular-nums">{tenant.subscriberCount}</td>
                <td className="text-paragraph-sm text-text-sub px-5 py-3">
                  {tenant.lastActivityAt ? new Date(tenant.lastActivityAt).toLocaleDateString() : '—'}
                </td>
                <td className="text-paragraph-sm text-text-sub px-5 py-3">
                  {new Date(tenant.createdAt).toLocaleDateString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between">
          <span className="text-paragraph-xs text-text-soft">
            Page {page + 1} of {lastPage + 1} · {total} tenants
          </span>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              mode="outline"
              disabled={page === 0}
              onClick={() => setPage((current) => Math.max(0, current - 1))}
            >
              Previous
            </Button>
            <Button
              variant="secondary"
              mode="outline"
              disabled={page >= lastPage}
              onClick={() => setPage((current) => current + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      <CreateTenantSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        onCreated={(organizationId) => navigate(`/superadmin/tenants/${organizationId}`)}
      />
    </SuperAdminPage>
  );
}

export default SuperAdminTenantsPage;
